<!--
    The hover card of a calendar item (PlanupCalendar.vue owns one per calendar): after a short
    hover over a pill it opens beside it with the item's type, day and time, the customer's contacts
    (picture + name) and the details the tooltip endpoints return (סוג אירוע / מקום / אורחים / צוות /
    the status, סוג פגישה, סוג משימה). Holidays, google events, booking windows and personal items
    (אישי: whose, the notes) show what the item itself carries - nothing is fetched for them. Placed to the left of the pill, to its right
    when there is no room, below it on a narrow screen; always inside the viewport. The card takes no
    mouse events, so it never traps the pointer; touch devices never see it.
-->
<template>
    <div class="pcal-hover"
         v-show="visible"
         :class="classes"
         :style="{ top: top + 'px', left: left + 'px' }"
         ref="card">

        <div class="pcal-hover-head">
            <span class="pcal-hover-icon"><i :class="icon"></i></span>
            <span class="pcal-hover-type">{{ typeLabel }}</span>
            <span class="pcal-hover-when">{{ when }}</span>
        </div>

        <div class="pcal-hover-body">

            <div class="pcal-hover-title">{{ title }}</div>

            <div class="pcal-hover-loading"
                 v-if="loading">
                <i class="fa-solid fa-spinner fa-spin"></i>
            </div>

            <template v-else>

                <div class="pcal-hover-contacts"
                     v-if="contacts.length">
                    <div class="pcal-hover-contact"
                         v-for="contact in contacts"
                         :key="contact.id">
                        <img :src="avatarOf(contact)"
                             alt="">
                        <span>{{ nameOf(contact) }}</span>
                    </div>
                </div>

                <div class="pcal-hover-rows"
                     v-if="rows.length">
                    <div class="pcal-hover-row"
                         v-for="row in rows"
                         :key="row.label">
                        <i :class="row.icon"></i>
                        <span class="pcal-hover-k">{{ row.label }}</span>
                        <span class="pcal-hover-v">{{ row.value }}</span>
                    </div>
                </div>

                <div class="pcal-hover-status"
                     v-if="statusHtml"
                     v-html="statusHtml"></div>

            </template>

        </div>

        <div class="pcal-hover-foot"
             v-if="openable">
            לחיצה פותחת את הכרטיס
        </div>

    </div>
</template>
<script>
import axiosV2 from '../../core/http/axios_api_v2';
import { itemIcon, ITEM_TYPE_LABELS } from '../../services/calendar/calendar-colors';

const SHOW_DELAY = 220;
const CACHE_TTL = 2 * 60 * 1000;
const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

// what is fetched for the card, by item type (the same endpoints the old tooltip used)
const ENDPOINTS = {
    event: '/event/tooltip/',
    meeting: '/meeting/tooltip/',
    task: '/tasks/tooltip/',
};

function nameOfContact(contact) {
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || '';
}

export default {
    name: 'CalendarHoverCard',
    props: {
        // the calendar's items can't be opened (the date picker) - no "click opens" hint
        disableOpen: { default: false },
    },
    data() {
        return {
            visible: false,
            item: null,
            data: null,
            loading: false,
            top: 0,
            left: 0,
        };
    },
    computed: {

        classes() {
            return this.item ? [this.item.itemType].concat(this.item.colorClasses || []) : [];
        },

        icon() {
            return itemIcon(this.item ? this.item.itemType : null);
        },

        typeLabel() {
            return this.item ? (ITEM_TYPE_LABELS[this.item.itemType] || '') : '';
        },

        title() {
            return this.item ? (this.item.originalTitle || this.item.title || 'ללא שם') : '';
        },

        // יום שלישי · 13/10/2026 · 10:00–12:00 (the item's day lives in the UTC fields, see the grid)
        when() {
            if (!this.item)
                return '';
            const date = new Date(this.item.startDate);
            if (isNaN(date.getTime()))
                return this.item.time || '';
            let text = 'יום ' + DAY_NAMES[date.getUTCDay()] + ' · ' + date.getUTCDate() + '/' + (date.getUTCMonth() + 1) + '/' + date.getUTCFullYear();
            if (this.item.time)
                text += ' · ' + this.item.time + (this.item.endTime ? '–' + this.item.endTime : '');
            return text;
        },

        contacts() {
            const customer = this.data && this.data.customer;
            const contacts = customer && customer.contacts;
            return Array.isArray(contacts) ? contacts.slice(0, 4) : [];
        },

        rows() {

            if (!this.item)
                return [];

            const item = this.item;
            const data = this.data || {};
            const rows = [];
            const add = (label, value, icon) => {
                if (value)
                    rows.push({ label: label, value: value, icon: icon });
            };

            switch (item.itemType) {

                case 'event':
                    add('סוג אירוע', data.eventClass && data.eventClass.name ? data.eventClass.name : item.eventClass, 'fa-solid fa-champagne-glasses');
                    add('מקום', data.place && data.place.name ? data.place.name : item.place, 'fa-solid fa-location-dot');
                    add('אורחים', data.estimatedNumberOfGuests || item.guests, 'fa-solid fa-users');
                    add('צוות', item.crewMembers, 'fa-solid fa-user-group');
                    add('סטטוס', item.eventStatusName, 'fa-regular fa-flag');
                    break;

                case 'meeting':
                    add('סטטוס', this.meetingStatusName(data, item), 'fa-regular fa-flag');
                    //the customer pressed אישור הגעה on the meeting's link (the tooltip's fresh row)
                    add('אישור הגעה', planup.$handlers.meetingHandler.customerConfirmedAt(data) ? 'הלקוח אישר' : '', 'fa-regular fa-circle-check');
                    add('סוג פגישה', data.meetingType && data.meetingType.name ? data.meetingType.name : item.meetingType, 'fa-regular fa-handshake');
                    add('מקום', data.place && data.place.name ? data.place.name : item.place, 'fa-solid fa-location-dot');
                    add('צוות', Array.isArray(data.crewMembers) && data.crewMembers.length ? data.crewMembers.map(member => member.name).join(', ') : item.crewMembers, 'fa-solid fa-user-group');
                    //who set the meeting (meeting/tooltip carries createdByName + bookedOnline)
                    add('נקבעה על ידי', data.bookedOnline ? 'זימון אונליין' + (data.createdByName ? ' (אישר/ה: ' + data.createdByName + ')' : '') : data.createdByName, 'fa-regular fa-user');
                    break;

                case 'task':
                    add('סוג משימה', data.taskType && data.taskType.name, 'fa-regular fa-circle-check');
                    add('מצב', data.done ? 'בוצעה' : (this.data ? 'פתוחה' : ''), 'fa-regular fa-square-check');
                    add('צוות', item.crewMembers, 'fa-solid fa-user-group');
                    break;

                case 'personal':
                    add('של', item.userName, 'fa-regular fa-user');
                    add('מתי', item.allDay ? 'כל היום' : '', 'fa-regular fa-clock');
                    add('חוזר', item.recurrenceText, 'fa-solid fa-rotate');
                    add('הערות', item.notes, 'fa-regular fa-note-sticky');
                    add('תיאום פגישות', item.blocksBooking ? 'הזמן חסום' : 'הזמן פנוי', 'fa-regular fa-calendar-check');
                    break;
            }

            return rows;
        },

        // the פניה's state as the customers table draws it
        statusHtml() {
            if (!this.item || this.item.itemType != 'event' || !this.item.planupSystemStatus)
                return '';
            try {
                return planup.$handlers.eventHandler.eventStatusIndicatorHTML(this.item.planupSystemStatus) || '';
            } catch (e) {
                return '';
            }
        },

        openable() {
            return !this.disableOpen && !!(this.item && (ENDPOINTS[this.item.itemType] || this.item.itemType == 'personal'));
        },
    },
    methods: {

        // סטטוס הפגישה: the tooltip's (fresh) meetingStatusId, or what the calendar loaded
        meetingStatusName(data, item) {
            if (data && data.meetingStatusId) {
                const status = planup.$handlers.meetingHandler.statusOf(data);
                if (status)
                    return status.name;
            }
            return item.meetingStatusName || '';
        },

        // called on the pill's mouseenter: the card opens after a short pause
        schedule(item, element) {
            this.cancel();
            if (planup.$globals.isMobile)
                return;
            this.timer = setTimeout(() => this.show(item, element), SHOW_DELAY);
        },

        cancel() {
            if (this.timer)
                clearTimeout(this.timer);
            this.timer = null;
        },

        show(item, element) {
            this.item = item;
            this.anchor = element;
            this.visible = true;
            this.load(item);
            this.$nextTick(this.place);
        },

        hide() {
            this.cancel();
            this.visible = false;
        },

        // the page moved: an open card is left behind its pill, so it closes; a hover that just
        // started (the timer) still opens
        onScroll() {
            if (this.visible)
                this.visible = false;
        },

        load(item) {

            const url = ENDPOINTS[item.itemType];
            const id = (item.id || '').toString().split('_')[1];

            this.data = null;

            if (!url || !id) {
                this.loading = false;
                return;
            }

            const cached = this.cache[item.id];
            if (cached && Date.now() - cached.at < CACHE_TTL) {
                this.data = cached.data;
                this.loading = false;
                return;
            }

            this.loading = true;
            const requested = item.id;

            axiosV2.get(url + id).then(response => {
                this.cache[item.id] = { at: Date.now(), data: response.data };
                if (this.item && this.item.id == requested) {
                    this.data = response.data;
                    this.loading = false;
                    this.$nextTick(this.place);
                }
            }).catch(() => {
                if (this.item && this.item.id == requested)
                    this.loading = false;
            });
        },

        // beside the pill (RTL: first to its left), below it when the screen is too narrow for that
        place() {

            if (!this.visible || !this.anchor || !this.$refs.card)
                return;

            const rect = this.anchor.getBoundingClientRect();
            const card = this.$refs.card.getBoundingClientRect();
            const vw = window.innerWidth, vh = window.innerHeight;
            const gap = 8;
            let left, top = rect.top - 4;

            if (rect.left - gap - card.width >= 8) {
                left = rect.left - gap - card.width;
            } else if (rect.right + gap + card.width <= vw - 8) {
                left = rect.right + gap;
            } else {
                left = Math.max(8, Math.min(rect.right - card.width, vw - card.width - 8));
                top = rect.bottom + 6;
                if (top + card.height > vh - 8)
                    top = rect.top - card.height - 6;
            }

            this.top = Math.max(8, Math.min(top, vh - card.height - 8));
            this.left = left;
        },

        avatarOf(contact) {
            return planup.$handlers.customerHandler.getContactImage(contact);
        },

        nameOf(contact) {
            return nameOfContact(contact);
        },
    },
    created() {
        this.cache = {};
        this.timer = null;
        this.anchor = null;
    },
    mounted() {
        window.addEventListener('scroll', this.onScroll, true);
        window.addEventListener('resize', this.onScroll);
    },
    beforeDestroy() {
        this.cancel();
        window.removeEventListener('scroll', this.onScroll, true);
        window.removeEventListener('resize', this.onScroll);
    },
}
</script>
<style>
.pcal-hover {
    position: fixed;
    z-index: 2000;
    width: 290px;
    max-width: calc(100vw - 16px);
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, .18), 0 0 0 1px rgba(15, 23, 42, .06);
    direction: rtl;
    text-align: right;
    overflow: hidden;
    font-size: 13px;
    color: #334155;
    pointer-events: none;
}

.pcal-hover-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    background: rgba(var(--pc-rgb), .14);
    color: var(--pc-ink);
    font-size: 12.5px;
    font-weight: 600;
}

.pcal-hover-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(var(--pc-rgb), .22);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex: 0 0 24px;
}

.pcal-hover-when {
    margin-right: auto;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    opacity: .9;
    white-space: nowrap;
}

.pcal-hover-body {
    padding: 10px 12px 6px;
}

.pcal-hover-title {
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    color: #0f172a;
    margin-bottom: 8px;
}

.pcal-hover-loading {
    padding: 4px 0 10px;
    text-align: center;
    color: #94a3b8;
}

.pcal-hover-contacts {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-bottom: 8px;
}

.pcal-hover-contact {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    color: #1e293b;
}

.pcal-hover-contact img {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    object-fit: cover;
    background: #f1f5f9;
}

.pcal-hover-rows {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 6px;
}

.pcal-hover-row {
    display: flex;
    align-items: baseline;
    gap: 7px;
    min-width: 0;
}

.pcal-hover-row i {
    flex: 0 0 14px;
    width: 14px;
    text-align: center;
    font-size: 11px;
    color: #94a3b8;
}

.pcal-hover-k {
    flex: 0 0 auto;
    color: #64748b;
}

.pcal-hover-v {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
    color: #1e293b;
}

.pcal-hover-status {
    margin: 2px 0 6px;
}

.pcal-hover-status .btn {
    pointer-events: none;
}

.pcal-hover-foot {
    padding: 6px 12px;
    border-top: 1px solid #f1f5f9;
    font-size: 11px;
    color: #94a3b8;
}
</style>
